import {
  TVA_RATES, TVA_DEDUCTIBLE_CATEGORIES, TAX_REGIMES, TAX_PENALTIES,
  IS_INSTALLMENT_MONTHS, CFE_CONFIG, calculateIS, getVehicleTonnage, getEssieuRate, TAXE_ESSIEU,
  TAX_OPTIMIZATIONS, CVAE_CONFIG,
  getEffectiveTvaFrequency, TVA_QUARTERLY_OPTION_THRESHOLD,
} from '../../data/taxes';
import { INVESTMENTS } from '../../data/investments';
import { computeFleetDepreciation } from '../../utils/depreciation';
import { computeCapexDepreciation } from '../../utils/capexDepreciation';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';
import { generateId } from '../helpers';
import { buildMarchettiRantMessage } from '../helpers/marchettiRantMessage';
import { SUPPORT_ROLES } from '../../data/supportRoles';
import {
  computeUrssafEstimate,
  isUrssafReminderDay,
  isUrssafDeclarationDay,
  quarterLabelFromDeclarationMonth,
} from '../helpers/urssaf';

// 14/06/2026 — Finance support roles allowed to pay TVA/IS on the player's
// behalf. Ordered by seniority: the DAF signs first, then the Comptable.
const TAX_DELEGATE_ROLE_IDS = ['role-pierre-marchetti', 'role-sophie-lambert'];

export const createTaxSlice = (set, get) => ({
  // Tax regime
  taxRegime: 'reel_simplifie',

  // TVA tracking
  tvaCollectedTotal: 0,    // Cumul TVA collectée this period
  tvaDeductibleTotal: 0,   // Cumul TVA déductible this period
  tvaBalance: 0,           // Net TVA to pay (or credit)
  tvaHistory: [],          // [{month, year, collected, deductible, paid, penalty}]
  tvaPendingDeclaration: false,
  tvaCredit: 0,            // Accumulated TVA credit
  // 13/06/2026 — Options TVA (réglementation française) :
  //   tvaPaymentBasis : 'encaissement' (défaut services) | 'debits' (option)
  //   tvaDeferredCollected : TVA collectée de la période précédente, non
  //     encore exigible quand on est à l'encaissement (clients à 30 j).
  //   tvaQuarterlyOption : option trimestrielle au réel normal (TVA < 4 000 €/an).
  tvaPaymentBasis: 'encaissement',
  tvaDeferredCollected: 0,
  tvaQuarterlyOption: false,
  // DGFiP étalement (max 6 months, 0,4 %/mo majoration).
  tvaInstallmentSchedule: [],         // [{ index, dueMonth, dueYear, amount, surcharge, paid }]
  tvaInstallmentUsedThisYear: false,  // Resets at year change.
  // Monthly profit snapshots — captured at each month rollover so the
  // Fiscalité chart can render the evolution + the IS slope.
  // Shape: [{ month: 1..12, year, profitCumul, isEstAnnual }]
  monthlyProfitHistory: [],
  // Cumulative depreciation (dotation aux amortissements) since the start
  // of the current fiscal year — reduces `annualProfit` daily.
  annualDepreciation: 0,

  // IS tracking
  annualProfit: 0,         // Running annual profit
  isInstallmentsPaid: [],  // [{ quarter, amount, month, year }]
  isPendingInstallment: false,
  isAnnualDue: 0,
  estimatedAnnualIS: 0,

  // Local taxes
  cfePaid: false,
  cfeDue: 0,
  cvaeDue: 0,
  essieuDue: 0,
  essieuPaid: [],          // [{ semester, amount }]

  // Optimizations active
  activeOptimizations: {},  // { [optId]: true }

  // Tax events & audit
  taxAlerts: [],
  lastAuditDay: 0,
  auditResult: null,       // 'clean' | 'minor' | 'major' | null

  // International (Phase 4)
  hasEuVatNumber: false,
  euVatRevenue: 0,
  customsDutyPaid: 0,

  // ── 08/02/2026 — URSSAF cotisations sociales (Q2 user prod) ────────
  // Pédagogique only — la masse salariale (employerCost daily) inclut
  // déjà les cotisations sociales dans le modèle financier. Ce flux
  // matérialise le rythme administratif réel français : reminder à J-15
  // de l'échéance trimestrielle (jours 15 mars/juin/sept/déc) et
  // déclaration officialisée le 1er du mois suivant (jours 1 jan/avr/
  // juil/oct). Aucune nouvelle ponction trésorerie.
  urssafHistory: [],                  // [{ quarter, year, amount, headcount, declaredOnDay }]
  urssafLastReminderKey: null,        // "2026-Q1" : évite double-envoi
  urssafLastDeclarationKey: null,     // "2026-Q1" : idem

  // ── 30/05/2026 — Midal perk: auto-provisioning of TVA + IS ──
  // When ON (only available if Tristan Midal is hired), each daily tax
  // accrual siphons the corresponding cash from `treasury` into a dedicated
  // reserve. On TVA declaration / IS instalment, the reserve pays first
  // and any surplus is refunded to treasury (bonus). No more nasty surprise
  // at year-end and the player feels Midal's value.
  taxAutoProvisioningEnabled: false,
  tvaProvisionedBalance: 0,   // Cash earmarked for TVA (not spendable).
  isProvisionedBalance: 0,    // Cash earmarked for IS (not spendable).

  // ── 30/05/2026 — Optional 25 % fiscal reserve (Livret A 3 % APY) ──
  // Mensuelle, sur le bénéfice net. Le compte est libéré à la clôture
  // annuelle IS (auto top-up). Intérêts crédités à chaque clôture mensuelle.
  fiscalReserveEnabled: false,
  fiscalReserveBalance: 0,
  // Total intérêts cumulés du Livret A — purement informatif pour l'UI.
  fiscalReserveInterestAccrued: 0,
  // Annual fiscal-reserve interest rate (Livret A indexed, default 3 %).
  fiscalReserveRate: 0.03,

  // ── 31/05/2026 — Midal rant counter ──
  // Increments when the player suffers an IS cash-flow incident WITHOUT
  // having enabled the auto-provisioning toggle. Resets at year start.
  // Consumed by `buildMarchettiRantMessage` at the annual close to push
  // a pince-sans-rire reminder from Midal in the Messagerie.
  marchettiIsIncidentsThisYear: 0,

  // ── 14/06/2026 — Délégation du paiement TVA/IS (Comptable / DAF) ──
  // When a finance support role (Tristan Midal — DAF, or Sophie Lambert —
  // Comptable) is on the payroll, the delegate automatically files & pays
  // the TVA declaration and the IS instalments at their deadline, provided
  // the provisions or the treasury cover them. Default ON — the toggle
  // becomes effective as soon as a delegate is hired.
  taxAutoPayEnabled: true,

  // ===== ACTIONS =====

  setTaxRegime: (regime) => {
    set({ taxRegime: regime });
  },

  // ── 13/06/2026 — Options TVA ─────────────────────────────────────────
  /**
   * Bascule entre TVA à l'encaissement (défaut services) et TVA sur les
   * débits. En passant aux débits, la TVA différée devient immédiatement
   * exigible (elle rejoint le cumul de la période en cours).
   */
  setTvaPaymentBasis: (basis) => {
    const state = get();
    if (!['encaissement', 'debits'].includes(basis)) return false;
    if ((state.tvaPaymentBasis || 'encaissement') === basis) return true;
    const patch = { tvaPaymentBasis: basis };
    if (basis === 'debits' && (state.tvaDeferredCollected || 0) > 0) {
      patch.tvaCollectedTotal = (state.tvaCollectedTotal || 0) + state.tvaDeferredCollected;
      patch.tvaDeferredCollected = 0;
    }
    patch.taxAlerts = [...(state.taxAlerts || []), {
      id: generateId(), type: 'tva_basis_changed',
      message: basis === 'debits'
        ? 'Option « TVA sur les débits » activée : la TVA est exigible dès la facturation. Mention obligatoire sur vos factures — vos clients B2B déduisent plus tôt.'
        : 'Retour à la « TVA à l\'encaissement » (défaut services) : la TVA n\'est due qu\'une fois vos clients encaissés — votre prochaine déclaration portera sur la période précédente.',
      day: state.totalDays, severity: 'info',
    }].slice(-20);
    set(patch);
    return true;
  },

  /**
   * Option trimestrielle au réel normal (tolérance TVA < 4 000 €/an).
   * Auto-révoquée à la déclaration si le seuil annuel est dépassé.
   */
  setTvaQuarterlyOption: (enabled) => {
    const state = get();
    set({
      tvaQuarterlyOption: !!enabled,
      taxAlerts: [...(state.taxAlerts || []), {
        id: generateId(), type: 'tva_rhythm_changed',
        message: enabled
          ? `Option trimestrielle activée (tolérance DGFiP : TVA exigible < ${TVA_QUARTERLY_OPTION_THRESHOLD.toLocaleString('fr-FR')} €/an). En cas de dépassement, retour automatique au rythme mensuel.`
          : 'Retour au rythme de déclaration mensuel (régime réel normal).',
        day: state.totalDays, severity: 'info',
      }].slice(-20),
    });
    return true;
  },

  // ─── Midal perk: toggle auto-provisioning. Returns the new state. ───
  toggleTaxAutoProvisioning: () => {
    const state = get();
    // Hard gate — only available when Tristan Midal is on the payroll.
    const hasMarchetti = !!(state.hiredSupport && state.hiredSupport['role-pierre-marchetti']);
    if (!hasMarchetti) return { ok: false, reason: 'marchetti_required' };
    const next = !state.taxAutoProvisioningEnabled;
    set({ taxAutoProvisioningEnabled: next });
    return { ok: true, enabled: next };
  },

  // ─── 14/06/2026 — Délégation TVA/IS : delegate lookup + toggle ───
  /**
   * Returns the hired finance delegate (`{ id, name, role }`) — DAF first
   * (more senior), then Comptable — or `null` when neither is hired.
   */
  getTaxDelegate: () => {
    const hired = get().hiredSupport || {};
    const delegateId = TAX_DELEGATE_ROLE_IDS.find(id => hired[id]);
    if (!delegateId) return null;
    const def = SUPPORT_ROLES.find(r => r.id === delegateId);
    return def ? { id: def.id, name: def.name, role: def.role } : null;
  },

  /** Toggle the TVA/IS payment delegation. Gated on a hired Comptable/DAF. */
  toggleTaxAutoPay: () => {
    const state = get();
    if (!get().getTaxDelegate()) return { ok: false, reason: 'delegate_required' };
    const next = !state.taxAutoPayEnabled;
    set({ taxAutoPayEnabled: next });
    return { ok: true, enabled: next };
  },

  // ─── Réserve fiscale 25 % (Livret A) ───
  toggleFiscalReserve: () => {
    const state = get();
    const next = !state.fiscalReserveEnabled;
    set({ fiscalReserveEnabled: next });
    return { ok: true, enabled: next };
  },

  // Daily-tick: also lets us accrue the Livret A interest pro rata on the
  // current `fiscalReserveBalance`. Called from coreSlice.nextDay.
  accrueFiscalReserveInterestDaily: () => {
    const state = get();
    if (!state.fiscalReserveEnabled || (state.fiscalReserveBalance || 0) <= 0) return;
    const dailyRate = (state.fiscalReserveRate || 0.03) / 365;
    const dailyInterest = state.fiscalReserveBalance * dailyRate;
    set({
      fiscalReserveBalance: state.fiscalReserveBalance + dailyInterest,
      fiscalReserveInterestAccrued: (state.fiscalReserveInterestAccrued || 0) + dailyInterest,
    });
  },

  // Called from the monthly close hook (monthlyReportBuilder) — transfers
  // 25 % of the latest net month profit into the fiscal reserve. We accept
  // a `monthNetProfit` argument so the caller (which already computed it)
  // doesn't double-compute. No-op if the toggle is off or the month is in
  // the red.
  transferFiscalReserveFromMonthlyProfit: (monthNetProfit) => {
    const state = get();
    if (!state.fiscalReserveEnabled) return 0;
    if (!Number.isFinite(monthNetProfit) || monthNetProfit <= 0) return 0;
    if ((state.treasury || 0) <= 0) return 0;
    const target = Math.round(monthNetProfit * 0.25);
    const transfer = Math.min(target, state.treasury);
    if (transfer <= 0) return 0;
    set({
      treasury: state.treasury - transfer,
      fiscalReserveBalance: (state.fiscalReserveBalance || 0) + transfer,
    });
    return transfer;
  },

  toggleOptimization: (optId) => {
    const state = get();
    const opt = TAX_OPTIMIZATIONS[optId];
    if (!opt) return;
    if (opt.requiresRegime && state.taxRegime !== opt.requiresRegime) return;

    const newOpts = { ...state.activeOptimizations };
    if (newOpts[optId]) {
      delete newOpts[optId];
    } else {
      newOpts[optId] = true;
    }
    set({ activeOptimizations: newOpts });
  },

  requestEuVatNumber: () => {
    const state = get();
    if (state.phase < 4) return;
    set({
      hasEuVatNumber: true,
      treasury: state.treasury - 200, // Administrative cost
    });
  },

  // 18/06/2026 — audit TVA user : les achats CAPEX ponctuels (véhicules
  // neufs/occasion, investissements) créditent désormais la TVA déductible
  // de la période, comme les charges quotidiennes. Taux/récupérabilité
  // par catégorie : data/taxes.js (TVA_DEDUCTIBLE_CATEGORIES).
  recordDeductibleTva: (amountHT, category = 'investments') => {
    const state = get();
    const cat = TVA_DEDUCTIBLE_CATEGORIES[category] || TVA_DEDUCTIBLE_CATEGORIES.investments;
    const tva = Math.round((amountHT || 0) * (cat.rate || 0) * (cat.recoverable ?? 1));
    if (tva <= 0) return 0;
    set({ tvaDeductibleTotal: (state.tvaDeductibleTotal || 0) + tva });
    return tva;
  },

  // Calculate daily TVA (called from nextDay)
  calculateDailyTax: (dayRevenue, dayExpenses, expenseBreakdown) => {
    const state = get();
    const tvaRate = TVA_RATES.standard.rate;

    // TVA collectée on revenue (HT -> TTC)
    const tvaCollected = Math.round(dayRevenue * tvaRate);

    // TVA déductible on expenses
    let tvaDeductible = 0;

    // Fuel: 80% recoverable
    if (expenseBreakdown.fuel > 0) {
      tvaDeductible += Math.round(expenseBreakdown.fuel * tvaRate * TVA_DEDUCTIBLE_CATEGORIES.fuel.recoverable);
    }
    // Maintenance
    if (expenseBreakdown.maintenance > 0) {
      tvaDeductible += Math.round(expenseBreakdown.maintenance * tvaRate);
    }
    // Rent/depot
    if (expenseBreakdown.rent > 0) {
      tvaDeductible += Math.round(expenseBreakdown.rent * tvaRate);
    }
    // Leasing
    if (expenseBreakdown.leasing > 0) {
      tvaDeductible += Math.round(expenseBreakdown.leasing * tvaRate);
    }
    // Investments
    if (expenseBreakdown.investments > 0) {
      tvaDeductible += Math.round(expenseBreakdown.investments * tvaRate);
    }
    // Insurance & salaries: NOT deductible (no TVA)

    // ── Daily depreciation (amortissement) — extracted from the owned
    //   fleet so it reduces the bénéfice fiscal at the same rhythm as the
    //   accounting books. Lease vehicles are excluded (no asset to amortize).
    //   When `amortissement_accelere` optimisation is active, the duration
    //   shortens from 5 to 3 years → daily dotation grows × 5/3 ≈ 1,67.
    const depreciation = computeFleetDepreciation(
      state.vehicles || [],
      state.totalDays || 0,
      state.activeOptimizations || {}
    );

    // ── 28/05/2026 v2 — Extended CAPEX depreciation : Phase B.
    //   Adds the depreciation of non-vehicle CAPEX (depots 20y, office 9y,
    //   software 3y, website 2y) on top of the fleet. Same fiscal impact
    //   logic — the cumulated daily dotation reduces annualProfit.
    const capexDep = computeCapexDepreciation(state);
    const totalDailyDotation = depreciation.totalDailyDotation + capexDep.totalDailyDotation;

    // ── 30/05/2026 — Midal auto-provisioning ──
    // When ON, the daily TVA owed (collected − deductible) and a daily
    // share of the expected IS (25 % rate on the profit of the day) are
    // siphoned from `treasury` into dedicated reserves. The player sees
    // a smaller "available" cash but never gets the year-end IS surprise.
    let tvaProvDelta = 0;
    let isProvDelta = 0;
    if (state.taxAutoProvisioningEnabled) {
      const netTvaDaily = tvaCollected - tvaDeductible;
      // Provision-side: positive net → we owe → set aside cash.
      // Negative (TVA credit) → we'll receive cash later → no transfer
      // because the credit reduces the future bill (handled by payTva).
      if (netTvaDaily > 0) {
        tvaProvDelta = Math.min(netTvaDaily, Math.max(0, state.treasury));
      }
      const dailyProfit = dayRevenue - dayExpenses - totalDailyDotation;
      if (dailyProfit > 0) {
        // 25 % IS rate is the SARL standard band; close enough for daily
        // provisioning. The annual close still applies the real IS scale
        // and refunds the over-provision (Midal's headline bonus).
        const isDaily = Math.round(dailyProfit * 0.25);
        isProvDelta = Math.min(isDaily, Math.max(0, state.treasury - tvaProvDelta));
      }
    }

    set({
      tvaCollectedTotal: state.tvaCollectedTotal + tvaCollected,
      tvaDeductibleTotal: state.tvaDeductibleTotal + tvaDeductible,
      annualProfit: state.annualProfit + (dayRevenue - dayExpenses) - totalDailyDotation,
      // Mirror the cumulative annual dotation so the Finance UI doesn't
      // need to re-sum daily ticks.
      annualDepreciation: (state.annualDepreciation || 0) + totalDailyDotation,
      treasury: state.treasury - tvaProvDelta - isProvDelta,
      tvaProvisionedBalance: (state.tvaProvisionedBalance || 0) + tvaProvDelta,
      isProvisionedBalance: (state.isProvisionedBalance || 0) + isProvDelta,
    });

    return { tvaCollected, tvaDeductible, dailyDepreciation: totalDailyDotation };
  },

  // Pay TVA declaration
  payTvaDeclaration: () => {
    const state = get();
    // 13/06/2026 — Exigibilité selon la base choisie :
    //   encaissement (défaut) : on déclare la TVA collectée de la période
    //     PRÉCÉDENTE (buffer tvaDeferredCollected) ; la collecte de la
    //     période courante part dans le buffer.
    //   débits : tout est exigible immédiatement (buffer inclus, au cas où
    //     l'option vient d'être activée).
    const isEncaissement = (state.tvaPaymentBasis || 'encaissement') === 'encaissement';
    const collectedDue = isEncaissement
      ? (state.tvaDeferredCollected || 0)
      : (state.tvaCollectedTotal || 0) + (state.tvaDeferredCollected || 0);
    const netTva = collectedDue - state.tvaDeductibleTotal;
    const toPay = Math.max(0, netTva);
    const credit = Math.max(0, -netTva);

    // ── 30/05/2026 — Midal auto-provisioning: pay from reserve first ──
    let fromReserve = 0;
    let reserveRefundToTreasury = 0;
    if (state.taxAutoProvisioningEnabled) {
      fromReserve = Math.min(state.tvaProvisionedBalance || 0, toPay);
      if ((state.tvaProvisionedBalance || 0) > toPay) {
        // Over-provisioned → refund the excess to spendable treasury.
        reserveRefundToTreasury = (state.tvaProvisionedBalance || 0) - toPay;
      }
    }
    const shortfall = toPay - fromReserve;

    if (shortfall > 0 && state.treasury < shortfall) {
      // Can't afford — surface the official DGFiP "étalement" option (max
      // 6 months, +0.4 %/month majoration) instead of just penalising.
      // The action `requestTvaInstallment` actually performs the spread.
      set({
        taxAlerts: [...state.taxAlerts, {
          id: generateId(), type: 'tva_insufficient',
          message: `Fonds insuffisants pour la TVA (${Math.round(shortfall)} €). Pénalités à prévoir — vous pouvez aussi demander un échéancier DGFiP (6 mois, +0,4 %/mois).`,
          day: state.totalDays, severity: 'grave',
          actionable: 'tva_installment_offer',
          amount: Math.round(shortfall),
        }],
      });
      return false;
    }

    const entry = {
      month: state.month,
      year: state.year,
      collected: Math.round(collectedDue),
      deductible: Math.round(state.tvaDeductibleTotal),
      net: Math.round(netTva),
      paid: Math.round(toPay),
      credit: Math.round(credit),
      fromReserve: Math.round(fromReserve),
      reserveRefund: Math.round(reserveRefundToTreasury),
      basis: state.tvaPaymentBasis || 'encaissement',
    };

    // 13/06/2026 — Option trimestrielle (réel normal) : tolérée tant que la
    // TVA exigible annuelle < 4 000 €. On vérifie sur les 12 derniers mois
    // d'historique ; en cas de dépassement → retour mensuel automatique.
    const newHistory = [...state.tvaHistory, entry].slice(-24);
    let quarterlyOptionPatch = {};
    if (state.tvaQuarterlyOption) {
      const rolling = newHistory
        .filter(h => (state.year - h.year) * 12 + (state.month - h.month) < 12)
        .reduce((s, h) => s + Math.max(0, h.net || 0), 0);
      if (rolling > TVA_QUARTERLY_OPTION_THRESHOLD) {
        quarterlyOptionPatch = {
          tvaQuarterlyOption: false,
          taxAlerts: [...(state.taxAlerts || []), {
            id: generateId(), type: 'tva_rhythm_revoked',
            message: `Votre TVA exigible sur 12 mois dépasse ${TVA_QUARTERLY_OPTION_THRESHOLD.toLocaleString('fr-FR')} € : l'administration révoque l'option trimestrielle — retour à la déclaration mensuelle (régime réel normal).`,
            day: state.totalDays, severity: 'warning',
          }].slice(-20),
        };
      }
    }

    set({
      // Treasury net: −shortfall + tvaCredit applied + any over-provisioned refund.
      treasury: state.treasury - shortfall + state.tvaCredit + reserveRefundToTreasury,
      tvaProvisionedBalance: 0, // Either fully used or fully refunded.
      tvaHistory: newHistory,
      tvaCollectedTotal: 0,
      // À l'encaissement : la collecte de la période courante devient le
      // différé exigible à la prochaine déclaration.
      tvaDeferredCollected: isEncaissement ? Math.round(state.tvaCollectedTotal || 0) : 0,
      tvaDeductibleTotal: 0,
      tvaBalance: 0,
      tvaCredit: credit,
      tvaPendingDeclaration: false,
      ...quarterlyOptionPatch,
    });
    return true;
  },

  /**
   * ╭───────────────────────────────────────────────────────────────────╮
   * │ TVA INSTALLMENT (DGFiP étalement)                                 │
   * ├───────────────────────────────────────────────────────────────────┤
   * │ Real-world rule (BOI-CF-IOR) : in case of temporary hardship, the │
   * │ tax authority grants a payment plan up to 6 months with a 0,4 %/mo│
   * │ surcharge on the remaining balance. Limit : 1 request per fiscal  │
   * │ year. The request must be made BEFORE the legal deadline.         │
   * │                                                                   │
   * │ Implementation : we create 6 monthly instalments stored in        │
   * │ `tvaInstallmentSchedule`. Each month the player must pay the      │
   * │ instalment — handled by `payTvaInstallmentTick` called from the   │
   * │ monthly cycle (next agent task — for now the schedule is created).│
   * │ The TVA declaration is marked as paid so the standard penalty     │
   * │ logic doesn't fire.                                               │
   * ╰───────────────────────────────────────────────────────────────────╯
   */
  requestTvaInstallment: () => {
    const state = get();
    const netTva = state.tvaCollectedTotal - state.tvaDeductibleTotal;
    const toPay = Math.max(0, netTva);
    if (toPay <= 0) return { ok: false, reason: 'Aucune TVA due.' };
    if (state.tvaInstallmentUsedThisYear) {
      return { ok: false, reason: 'Un échéancier TVA a déjà été accordé cette année.' };
    }
    const monthlyAmount = Math.ceil(toPay / 6);
    const schedule = [];
    for (let i = 1; i <= 6; i++) {
      // 0,4 %/month surcharge applied to the *remaining* balance (compound).
      const surcharge = Math.round(monthlyAmount * 0.004 * i);
      schedule.push({
        index: i,
        dueMonth: ((state.month + i - 1) % 12) + 1,
        dueYear: state.year + Math.floor((state.month + i - 1) / 12),
        amount: monthlyAmount,
        surcharge,
        paid: false,
      });
    }
    set({
      tvaInstallmentSchedule: schedule,
      tvaInstallmentUsedThisYear: true,
      tvaCollectedTotal: 0,
      tvaDeductibleTotal: 0,
      tvaBalance: 0,
      tvaPendingDeclaration: false,
      taxAlerts: [...state.taxAlerts, {
        id: generateId(), type: 'tva_installment_granted',
        message: `Échéancier TVA accordé : 6 mensualités de ${monthlyAmount} € + majoration 0,4 %/mois.`,
        day: state.totalDays, severity: 'modéré',
      }],
    });
    return { ok: true, monthlyAmount, totalSurcharge: Math.round(toPay * 0.024) };
  },

  /**
   * Called by `checkTaxDeadlines` at the start of each new month: settles
   * the next due TVA instalment if any. Returns the amount actually paid
   * so the UI can notify the player.
   */
  payNextTvaInstallment: () => {
    const state = get();
    const schedule = state.tvaInstallmentSchedule || [];
    if (schedule.length === 0) return 0;
    const next = schedule.find(s => !s.paid && (s.dueYear < state.year || (s.dueYear === state.year && s.dueMonth <= state.month)));
    if (!next) return 0;
    const due = next.amount + next.surcharge;
    if (state.treasury < due) {
      set({
        taxAlerts: [...state.taxAlerts, {
          id: generateId(), type: 'tva_installment_late',
          message: `Mensualité TVA en retard (${due} €). Pénalités possibles.`,
          day: state.totalDays, severity: 'grave',
        }],
      });
      return 0;
    }
    set({
      treasury: state.treasury - due,
      tvaInstallmentSchedule: schedule.map(s => s.index === next.index ? { ...s, paid: true, paidDay: state.totalDays } : s),
    });
    return due;
  },

  // Pay IS installment
  payIsInstallment: () => {
    const state = get();
    const quarter = Math.ceil(state.month / 3);

    // IS optimization reductions
    let reductionRate = 0;
    Object.keys(state.activeOptimizations).forEach(optId => {
      const opt = TAX_OPTIMIZATIONS[optId];
      if (opt?.isReduction) reductionRate += opt.isReduction;
    });
    // Support role tax reduction stacks with optimizations (e.g. Sophie Lambert)
    if (typeof get().getSupportTaxReduction === 'function') {
      reductionRate += get().getSupportTaxReduction();
    }

    // Credit d'impot formation
    let creditFormation = 0;
    if (state.activeOptimizations.credit_impot_formation) {
      creditFormation = (state.drivers?.length || 0) * (TAX_OPTIMIZATIONS.credit_impot_formation.creditPerDriver || 0);
    }

    const estimatedAnnualProfit = projectAnnualProfit(state).annualProfit;
    const estimatedIS = calculateIS(estimatedAnnualProfit);
    const adjustedIS = Math.max(0, Math.round(estimatedIS * (1 - reductionRate) - creditFormation));
    const installment = Math.round(adjustedIS / 4);

    // ── 30/05/2026 — Auto-provisioning: pay from IS reserve first, then ──
    //    fiscal-reserve top-up, then spendable treasury. The lazy refund of
    //    the over-provision happens at the annual close.
    // ── 25/06/2026 hotfix bug user — l'alerte « fonds insuffisants » se
    //    déclenchait à tort quand la provision (isProvisionedBalance) ou
    //    la réserve fiscale couvrait le montant, parce que le tap-into
    //    était conditionné au toggle `taxAutoProvisioningEnabled`. Désor-
    //    mais, dès qu'une provision/réserve existe, on l'utilise auto-
    //    matiquement pour régler l'acompte — le toggle ne contrôle plus
    //    que l'incrément annuel de la provision, pas son usage à
    //    l'échéance. Si la trésorerie + les réserves couvrent l'acompte,
    //    aucune alerte.
    let fromIsReserve = Math.min(state.isProvisionedBalance || 0, installment);
    let remaining = installment - fromIsReserve;
    let fromFiscalReserve = 0;
    if (remaining > 0 && (state.fiscalReserveBalance || 0) > 0) {
      fromFiscalReserve = Math.min(state.fiscalReserveBalance, remaining);
      remaining -= fromFiscalReserve;
    }

    if (state.treasury < remaining) {
      // Surface the bank credit offer when the company is otherwise healthy
      // (annualProfit > 0). Real banks routinely fund tax payments for
      // profitable companies — modelled as a 6-month loan at 4,5 % annual.
      // The standard penalty alert is still raised so a refusal of the
      // credit still incurs the existing penalty.
      const profitable = (state.annualProfit || 0) > 0;
      // 31/05/2026 — Track incidents for the Midal rant. Only counts
      // when the auto-provisioning toggle is OFF (incidents while ON
      // are not Midal's fault — the reserve covered the bill).
      const incidentBump = state.taxAutoProvisioningEnabled ? 0 : 1;
      set({
        marchettiIsIncidentsThisYear: (state.marchettiIsIncidentsThisYear || 0) + incidentBump,
        taxAlerts: [...state.taxAlerts, {
          id: generateId(), type: 'is_insufficient',
          message: profitable
            ? `Fonds insuffisants pour l'acompte IS T${quarter} (${remaining} €). Votre banque peut vous proposer un crédit fiscal court terme — voir la Banque.`
            : `Fonds insuffisants pour l'acompte IS T${quarter} (${remaining} €).`,
          day: state.totalDays, severity: 'grave',
          actionable: profitable ? 'is_bank_credit_offer' : null,
          amount: remaining,
        }],
      });
      return false;
    }

    set({
      treasury: state.treasury - remaining,
      isProvisionedBalance: Math.max(0, (state.isProvisionedBalance || 0) - fromIsReserve),
      fiscalReserveBalance: Math.max(0, (state.fiscalReserveBalance || 0) - fromFiscalReserve),
      isInstallmentsPaid: [...state.isInstallmentsPaid, {
        quarter, amount: installment, month: state.month, year: state.year,
        fromIsReserve, fromFiscalReserve,
      }],
      isPendingInstallment: false,
      estimatedAnnualIS: adjustedIS,
    });
    return true;
  },

  /**
   * Take a 6-month bank credit dedicated to paying an unpaid IS instalment.
   * - Amount : the exact missing amount (capped at the outstanding instalment).
   * - Rate   : 4,5 % annual (non-secured short-term loan).
   * - Term   : 6 months, equal monthly payments wired via the bank slice.
   *
   * Requires the company to be profitable (annualProfit > 0) — losing
   * companies don't get bank financing for tax payments. Returns
   * `{ ok, reason, loan }`.
   */
  takeIsBankCredit: () => {
    const state = get();
    if ((state.annualProfit || 0) <= 0) {
      return { ok: false, reason: "Crédit refusé : l'entreprise n'est pas rentable cette année." };
    }
    // Re-compute the required instalment (same logic as payIsInstallment).
    let reductionRate = 0;
    Object.keys(state.activeOptimizations || {}).forEach(optId => {
      const opt = TAX_OPTIMIZATIONS[optId];
      if (opt?.isReduction) reductionRate += opt.isReduction;
    });
    if (typeof get().getSupportTaxReduction === 'function') {
      reductionRate += get().getSupportTaxReduction();
    }
    const estimatedAnnualProfit = projectAnnualProfit(state).annualProfit;
    const estimatedIS = calculateIS(estimatedAnnualProfit);
    const adjustedIS = Math.max(0, Math.round(estimatedIS * (1 - reductionRate)));
    const installment = Math.round(adjustedIS / 4);
    // 25/06/2026 hotfix — compute the missing amount taking into account
    // the provisioned reserve and the fiscal reserve, not just treasury.
    // Avoids surfacing a bank-credit offer when reserves already cover.
    const availableCash = (state.treasury || 0)
      + (state.isProvisionedBalance || 0)
      + (state.fiscalReserveBalance || 0);
    const missing = Math.max(0, installment - availableCash);
    if (missing <= 0) {
      return { ok: false, reason: 'Aucun crédit nécessaire (trésorerie + provisions suffisantes).' };
    }
    // Take a fast-track 6-month loan dedicated to the IS payment. We bypass
    // the standard `acceptLoanProposal` flow because the bank pre-approves
    // tax-purpose credits for profitable companies — no comparison needed.
    const monthlyRate = 4.5 / 100 / 12;
    const monthlyPayment = Math.round(
      missing * monthlyRate / (1 - Math.pow(1 + monthlyRate, -6))
    );
    const loan = {
      id: generateId(),
      bankId: 'tax-credit',
      kind: 'is_credit',
      principal: missing,
      // 19/07/2026 — était stocké 4.5 (pourcent) alors que tout le moteur
      // attend un décimal (0.045) : l'amortissement calculait 37,5 %
      // d'intérêts PAR MOIS et le restant dû explosait.
      annualRate: 0.045,
      durationMonths: 6,
      monthlyPayment,
      remainingMonths: 6,
      remainingPrincipal: missing,
      startDay: state.totalDays,
      label: `Crédit fiscal IS T${Math.ceil(state.month / 3)}`,
    };
    set({
      treasury: state.treasury + missing,
      credits: [...(state.credits || []), loan],
    });
    // Re-attempt the IS payment (treasury now sufficient).
    const refreshed = get();
    if (refreshed.treasury >= installment) {
      get().payIsInstallment();
    }
    return { ok: true, loan: { amount: missing, rate: 4.5, termMonths: 6, monthlyPayment } };
  },

  // Pay CFE
  payCfe: () => {
    const state = get();
    // CFE based on vehicle fleet value approximation
    const fleetValue = (state.vehicles || []).reduce((s, v) => s + v.price, 0);
    let cfeAmount = Math.max(CFE_CONFIG.baseMinimum, Math.round(fleetValue * CFE_CONFIG.rateMultiplier));
    // 29/05/2026 — apply CFE exoneration regional aid if active.
    // The multiplier comes from leaseAidSlice (0.85 when aid-cfe-exoneration
    // is active, 1 otherwise).
    const cfeMultiplier = (typeof get().getCfeMultiplier === 'function')
      ? get().getCfeMultiplier() : 1;
    cfeAmount = Math.round(cfeAmount * cfeMultiplier);

    if (state.treasury < cfeAmount) {
      set({
        taxAlerts: [...state.taxAlerts, {
          id: generateId(), type: 'cfe_insufficient',
          message: `Fonds insuffisants pour la CFE (${cfeAmount} €).`,
          day: state.totalDays, severity: 'modéré',
        }],
      });
      return false;
    }

    set({
      treasury: state.treasury - cfeAmount,
      cfePaid: true,
      cfeDue: 0,
    });
    return true;
  },

  // Pay taxe essieu
  payTaxeEssieu: () => {
    const state = get();
    let totalEssieu = 0;
    (state.vehicles || []).forEach(v => {
      const tonnage = getVehicleTonnage(v.tier);
      const annualRate = getEssieuRate(tonnage);
      totalEssieu += Math.round(annualRate / 2); // Semi-annual
    });

    // 03/06/2026 — bug fix : la fonction retournait silencieusement true/false
    // sans feedback utilisateur. On pousse maintenant une entrée taxAlerts
    // (succès ou échec) pour que le joueur comprenne l'action.
    if (totalEssieu === 0) {
      // Plus aucun véhicule > 3,5 T → nettoyer l'essieuDue résiduel et
      // notifier le joueur de l'auto-clear.
      set({
        essieuDue: 0,
        taxAlerts: [...(state.taxAlerts || []), {
          id: generateId(), type: 'essieu_no_vehicle',
          message: 'Aucun véhicule > 3,5 T : taxe à l\'essieu annulée.',
          day: state.totalDays, severity: 'info',
        }],
      });
      return true;
    }
    if (state.treasury < totalEssieu) {
      set({
        taxAlerts: [...(state.taxAlerts || []), {
          id: generateId(), type: 'essieu_insufficient',
          message: `Fonds insuffisants pour la taxe à l'essieu (${totalEssieu} €). Trésorerie actuelle : ${Math.round(state.treasury)} €.`,
          day: state.totalDays, severity: 'modéré',
        }],
      });
      return false;
    }

    const semester = state.month <= 6 ? 1 : 2;
    set({
      treasury: state.treasury - totalEssieu,
      essieuPaid: [...state.essieuPaid, { semester, amount: totalEssieu, year: state.year }],
      essieuDue: 0,
      taxAlerts: [...(state.taxAlerts || []), {
        id: generateId(), type: 'essieu_paid',
        message: `Taxe à l'essieu payée : ${totalEssieu} € (semestre ${semester}/${state.year}).`,
        day: state.totalDays, severity: 'info',
      }],
    });
    return true;
  },

  // Check for tax deadlines at month change (called from nextDay)
  checkTaxDeadlines: () => {
    const state = get();
    const regime = TAX_REGIMES[state.taxRegime];
    if (!regime) return;

    // ── Snapshot the bénéfice + IS estimé at the END of the previous month.
    // We do this BEFORE the year reset so December's snapshot lands in
    // history with the full annualProfit. The IS chart consumes this array.
    if (typeof state.annualProfit === 'number') {
      // The month we're closing is the one currently on `state.month` (this
      // function runs on the FIRST day of the new month — `state.month` has
      // already been incremented by `nextDay`, so the just-finished month
      // is `state.month - 1` mod 12).
      let closingMonth = state.month - 1;
      let closingYear = state.year;
      if (closingMonth < 1) {
        closingMonth = 12;
        closingYear = state.year - 1;
      }
      const estimatedAnnualProfit = projectAnnualProfit({
        annualProfit: state.annualProfit,
        month: closingMonth,
      }).annualProfit;
      const isEst = calculateIS(estimatedAnnualProfit);
      const history = state.monthlyProfitHistory || [];
      const next = [
        ...history,
        {
          month: closingMonth,
          year: closingYear,
          profitCumul: Math.round(state.annualProfit),
          isEstAnnual: Math.round(isEst),
        },
      ].slice(-24); // Keep last 24 months max.
      set({ monthlyProfitHistory: next });
    }

    const newAlerts = [];

    // TVA declaration due?
    let tvaBecamePending = false;
    if (regime.tvaFrequency === 'monthly') {
      set({ tvaPendingDeclaration: true });
      tvaBecamePending = true;
    } else if (regime.tvaFrequency === 'quarterly' && [3, 6, 9, 12].includes(state.month)) {
      set({ tvaPendingDeclaration: true });
      tvaBecamePending = true;
    }

    // IS installment due?
    let isBecamePending = false;
    if (IS_INSTALLMENT_MONTHS.includes(state.month)) {
      set({ isPendingInstallment: true });
      isBecamePending = true;
    }

    // CFE due in December
    if (state.month === CFE_CONFIG.dueMonth && !state.cfePaid) {
      const fleetValue = (state.vehicles || []).reduce((s, v) => s + v.price, 0);
      let cfeAmount = Math.max(CFE_CONFIG.baseMinimum, Math.round(fleetValue * CFE_CONFIG.rateMultiplier));
      // 29/05/2026 — apply CFE exoneration regional aid (-15% if active)
      const cfeMultiplier = (typeof get().getCfeMultiplier === 'function')
        ? get().getCfeMultiplier() : 1;
      cfeAmount = Math.round(cfeAmount * cfeMultiplier);
      set({ cfeDue: cfeAmount });
    }

    // Taxe essieu in January / July
    if (TAXE_ESSIEU.dueMonths.includes(state.month)) {
      let totalEssieu = 0;
      (state.vehicles || []).forEach(v => {
        const tonnage = getVehicleTonnage(v.tier);
        totalEssieu += Math.round(getEssieuRate(tonnage) / 2);
      });
      if (totalEssieu > 0) set({ essieuDue: totalEssieu });
    }

    // Reset annual tracking at year start
    if (state.month === 1 && state.day === 1) {
      // ── 31/05/2026 — Midal pince-sans-rire rant ──
      // Fired BEFORE the bonus modal so a "good year with auto-prov on"
      // celebrates Midal, and a "bad year with auto-prov off" still
      // earns the same DAF a friendly scolding. Pure check + push.
      try {
        const rantState = get();
        const rant = buildMarchettiRantMessage(rantState);
        if (rant && typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage(rant);
        }
      } catch (err) {
        console.warn('[taxSlice] Midal rant failed:', err);
      }

      // ── 30/05/2026 — Year-end IS over-provision refund (Midal perk) ──
      // Any cash still sitting on the IS provision reserve after the last
      // instalment of the year was paid is a real surplus → return it to
      // spendable treasury. The Réserve fiscale 25 % is also released
      // (player can park it again next year if they want).
      const sFresh = get();
      const isOver = sFresh.taxAutoProvisioningEnabled ? (sFresh.isProvisionedBalance || 0) : 0;
      const reserveRelease = sFresh.fiscalReserveEnabled ? Math.round(sFresh.fiscalReserveBalance || 0) : 0;
      const interestEarned = sFresh.fiscalReserveEnabled ? Math.round(sFresh.fiscalReserveInterestAccrued || 0) : 0;
      const refundTotal = isOver + reserveRelease;
      if (refundTotal > 0) {
        set({
          treasury: sFresh.treasury + refundTotal,
          isProvisionedBalance: 0,
          fiscalReserveBalance: 0,
          fiscalReserveInterestAccrued: 0,
          // 30/05/2026 — Fire a celebratory modal so the player feels the
          // value Midal brings instead of seeing a faceless treasury
          // jump. Consumed by <MarchettiBonusModal /> in App.js.
          pendingMarchettiBonus: {
            isOver: Math.round(isOver),
            reserveRelease,
            interestEarned,
            year: sFresh.year,
            day: sFresh.totalDays,
          },
          taxAlerts: [...sFresh.taxAlerts, {
            id: generateId(), type: 'tax_reserve_refund',
            message: isOver > 0
              ? `Clôture annuelle : Midal rembourse ${isOver.toLocaleString('fr-FR')} € d'IS sur-provisionné${reserveRelease > 0 ? ` + ${reserveRelease.toLocaleString('fr-FR')} € de Réserve fiscale libérée` : ''}.`
              : `Clôture annuelle : Réserve fiscale libérée — ${reserveRelease.toLocaleString('fr-FR')} € reversés en trésorerie.`,
            day: sFresh.totalDays, severity: 'mineur',
            amount: refundTotal,
          }],
        });
      }
      set({
        annualProfit: 0,
        annualDepreciation: 0,
        isInstallmentsPaid: [],
        cfePaid: false,
        essieuPaid: [],
        // DGFiP étalement quota resets at the new fiscal year.
        tvaInstallmentUsedThisYear: false,
        // 31/05/2026 — Midal rant counter resets each new fiscal year.
        marchettiIsIncidentsThisYear: 0,
      });
    }

    // Settle the next TVA instalment (if any) at the start of each month.
    if (typeof get().payNextTvaInstallment === 'function') {
      get().payNextTvaInstallment();
    }

    // ── 14/06/2026 — Délégation du paiement TVA/IS (Comptable / DAF) ──
    // The hired delegate files & pays the dues the day they fall due, IF
    // the provisions or the treasury can cover them. `payTvaDeclaration` /
    // `payIsInstallment` already settle from the reserves first and raise
    // the standard "insufficient" alert on failure — the pending flag then
    // stays up so the player can act manually (échéancier, crédit fiscal…).
    if ((tvaBecamePending || isBecamePending) && get().taxAutoPayEnabled) {
      const delegate = get().getTaxDelegate();
      if (delegate) {
        if (tvaBecamePending && get().payTvaDeclaration() === true) {
          const after = get();
          const lastTva = (after.tvaHistory || [])[(after.tvaHistory || []).length - 1];
          const paid = lastTva ? lastTva.paid : 0;
          set({
            taxAlerts: [...after.taxAlerts, {
              id: generateId(), type: 'tva_autopaid',
              message: paid > 0
                ? `${delegate.name} (${delegate.role}) a déclaré et payé la TVA : ${paid.toLocaleString('fr-FR')} €.`
                : `${delegate.name} (${delegate.role}) a déposé votre déclaration de TVA — aucun montant à payer.`,
              day: after.totalDays, severity: 'info',
              amount: paid,
            }].slice(-20),
          });
        }
        if (isBecamePending && get().payIsInstallment() === true) {
          const after = get();
          const lastIs = (after.isInstallmentsPaid || [])[(after.isInstallmentsPaid || []).length - 1];
          const amount = lastIs ? lastIs.amount : 0;
          set({
            taxAlerts: [...after.taxAlerts, {
              id: generateId(), type: 'is_autopaid',
              message: amount > 0
                ? `${delegate.name} (${delegate.role}) a payé l'acompte IS T${Math.ceil(state.month / 3)} : ${amount.toLocaleString('fr-FR')} €.`
                : `${delegate.name} (${delegate.role}) a validé l'acompte IS T${Math.ceil(state.month / 3)} — aucun montant dû.`,
              day: after.totalDays, severity: 'info',
              amount,
            }].slice(-20),
          });
        }
      }
    }

    // Random tax audit check
    const auditChance = regime.auditRisk || 0.03;
    if (state.totalDays > 60 && state.totalDays - (state.lastAuditDay || 0) > 90 && Math.random() < auditChance) {
      // Trigger audit based on tax compliance. Read FRESH state — the
      // auto-pay delegation above may have just settled the dues (and
      // moved treasury / alerts), so the stale `state` snapshot would
      // wrongly penalise the player or clobber the autopay mutations.
      const sAudit = get();
      const hasPendingTva = sAudit.tvaPendingDeclaration;
      const hasPendingIs = sAudit.isPendingInstallment;
      const overdueTaxes = hasPendingTva || hasPendingIs;

      let auditResult = 'clean';
      let penalty = 0;
      let repEffect = 0;

      if (overdueTaxes && Math.random() < 0.7) {
        auditResult = Math.random() < 0.4 ? 'major' : 'minor';
        const penaltyDef = TAX_PENALTIES[`audit_${auditResult}`];
        penalty = penaltyDef.fine;
        repEffect = -(penaltyDef.reputationLoss);
      } else {
        repEffect = TAX_PENALTIES.audit_clean.reputationGain;
      }

      newAlerts.push({
        id: generateId(),
        type: 'audit',
        message: auditResult === 'clean'
          ? 'Contrôle fiscal terminé sans anomalie. Félicitations !'
          : `Contrôle fiscal : ${auditResult === 'major' ? 'infraction majeure' : 'infraction mineure'}. Amende de ${penalty} €.`,
        day: state.totalDays,
        severity: auditResult === 'clean' ? 'mineur' : 'grave',
      });

      set({
        lastAuditDay: state.totalDays,
        auditResult,
        treasury: sAudit.treasury - penalty,
        reputation: Math.max(0, Math.min(100, (sAudit.reputation || 50) + repEffect)),
        taxAlerts: [...sAudit.taxAlerts, ...newAlerts].slice(-20),
      });
    }
  },

  /**
   * 08/02/2026 — URSSAF tick. Doit être appelé QUOTIDIENNEMENT depuis
   * `coreSlice.nextDay` (pas seulement au changement de mois). Gère :
   *   - Reminder à J-15 de l'échéance (jours 15 mars/juin/sept/déc)
   *   - Déclaration officielle au 1er du mois suivant (jours 1 jan/avr/
   *     juil/oct)
   *
   * Idempotent : utilise `urssafLastReminderKey` / `urssafLastDeclarationKey`
   * pour éviter les doubles envois si la fonction est invoquée plusieurs
   * fois le même jour (réplique nextDay race condition).
   */
  checkUrssafDeadlines: () => {
    const state = get();
    const day = state.day;
    const month = state.month;
    const year = state.year;
    if (!day || !month) return;

    // ─── Reminder J-15 ────────────────────────────────────────────────
    if (isUrssafReminderDay(month, day)) {
      // The quarter ending this month → declaration in the next month.
      const declarationMonth = (month % 12) + 1;
      const declarationYear = month === 12 ? year + 1 : year;
      const meta = quarterLabelFromDeclarationMonth(declarationMonth, declarationYear);
      const remKey = meta ? `${meta.year}-Q${meta.quarter}` : `${year}-${month}`;
      if (state.urssafLastReminderKey !== remKey
        && typeof get().pushCustomMessage === 'function') {
        get().pushCustomMessage({
          kind: 'admin',
          senderName: 'URSSAF Service Cotisations',
          senderRole: 'Service Cotisations',
          senderType: 'urssaf',
          subject: `URSSAF — Échéance déclaration trimestrielle ${meta ? meta.label : ''} dans 15 jours`,
          body: `Madame, Monsieur,\n\n`
            + `Nous vous rappelons l'échéance de votre déclaration trimestrielle dans 15 jours. Pensez à anticiper.\n\n`
            + `URSSAF Service Cotisations`,
          ttlDays: 20,
          category: 'admin',
        });
        set({ urssafLastReminderKey: remKey });
      }
    }

    // ─── Déclaration officielle (J+1 du début du nouveau trimestre) ──
    if (isUrssafDeclarationDay(month, day)) {
      const meta = quarterLabelFromDeclarationMonth(month, year);
      const decKey = meta ? `${meta.year}-Q${meta.quarter}` : `${year}-${month}`;
      if (state.urssafLastDeclarationKey !== decKey) {
        const est = computeUrssafEstimate(state);
        const entry = {
          quarter: meta ? meta.quarter : 0,
          year: meta ? meta.year : year,
          amount: est.quarterlyUrssaf,
          headcount: est.headcount,
          declaredOnDay: state.totalDays || 0,
        };
        // Auto-handled if a finance delegate is on payroll (Sophie Lambert
        // or Tristan Midal) — the player just sees a confirmation mail.
        const delegate = typeof get().getTaxDelegate === 'function' ? get().getTaxDelegate() : null;
        const delegateName = delegate ? `${delegate.name} (${delegate.role})` : null;
        set({
          urssafHistory: [...(state.urssafHistory || []), entry].slice(-20),
          urssafLastDeclarationKey: decKey,
        });
        if (typeof get().pushCustomMessage === 'function') {
          if (est.headcount === 0) {
            // No payroll → courtesy "néant" mail.
            get().pushCustomMessage({
              kind: 'admin',
              senderName: 'URSSAF Service Cotisations',
              senderRole: 'Service Cotisations',
              senderType: 'urssaf',
              subject: `URSSAF — Déclaration ${meta ? meta.label : ''} (néant)`,
              body: `Madame, Monsieur,\n\n`
                + `Aucune masse salariale déclarée pour le trimestre ${meta ? meta.label : ''}. Votre déclaration est validée à 0 €.\n\n`
                + `Bonne continuation,\nURSSAF Service Cotisations`,
              ttlDays: 30,
              category: 'admin',
            });
          } else {
            get().pushCustomMessage({
              kind: 'admin',
              senderName: 'URSSAF Service Cotisations',
              senderRole: 'Service Cotisations',
              senderType: 'urssaf',
              subject: `URSSAF — Déclaration ${meta ? meta.label : ''} validée`,
              body: `Madame, Monsieur,\n\n`
                + `Votre déclaration trimestrielle ${meta ? meta.label : ''} a été enregistrée :\n\n`
                + `   • Effectif déclaré : ${est.headcount} personne(s)\n`
                + `   • Masse salariale brute estimée : ${Math.round(est.monthlyPayroll * 3).toLocaleString('fr-FR')} €\n`
                + `   • Cotisations URSSAF (≈ 42 %) : **${est.quarterlyUrssaf.toLocaleString('fr-FR')} €**\n\n`
                + `Ce montant a été prélevé au fil de l'eau dans votre masse salariale quotidienne (employerCost). `
                + `Aucun débit supplémentaire n'est appliqué.\n\n`
                + (delegateName
                  ? `Déclaration télé-transmise par ${delegateName} pour votre compte. Vous pouvez consulter le détail dans l'onglet Fiscalité.`
                  : `Pensez à conserver ce justificatif pour votre comptabilité.`)
                + `\n\nURSSAF Service Cotisations`,
              ttlDays: 30,
              category: 'admin',
            });
          }
        }
      }
    }
  },

  // Reset tax state for new game
  resetTaxState: () => {
    set({
      taxRegime: 'reel_simplifie',
      tvaCollectedTotal: 0,
      tvaDeductibleTotal: 0,
      tvaBalance: 0,
      tvaHistory: [],
      tvaPendingDeclaration: false,
      tvaCredit: 0,
      tvaPaymentBasis: 'encaissement',
      tvaDeferredCollected: 0,
      tvaQuarterlyOption: false,
      tvaInstallmentSchedule: [],
      tvaInstallmentUsedThisYear: false,
      monthlyProfitHistory: [],
      annualDepreciation: 0,
      annualProfit: 0,
      isInstallmentsPaid: [],
      isPendingInstallment: false,
      isAnnualDue: 0,
      estimatedAnnualIS: 0,
      cfePaid: false,
      cfeDue: 0,
      cvaeDue: 0,
      essieuDue: 0,
      essieuPaid: [],
      activeOptimizations: {},
      taxAlerts: [],
      lastAuditDay: 0,
      auditResult: null,
      hasEuVatNumber: false,
      euVatRevenue: 0,
      customsDutyPaid: 0,
      // 30/05/2026 — auto-provisioning + fiscal reserve
      taxAutoProvisioningEnabled: false,
      tvaProvisionedBalance: 0,
      isProvisionedBalance: 0,
      fiscalReserveEnabled: false,
      fiscalReserveBalance: 0,
      fiscalReserveInterestAccrued: 0,
      // 31/05/2026 — Midal rant counter
      marchettiIsIncidentsThisYear: 0,
      // 14/06/2026 — délégation TVA/IS (ON par défaut dès qu'un délégué est embauché)
      taxAutoPayEnabled: true,
      // 08/02/2026 — URSSAF
      urssafHistory: [],
      urssafLastReminderKey: null,
      urssafLastDeclarationKey: null,
    });
  },
});
